/** IDs only. IndexedDB add() is the atomic cross-tab claim BEFORE popup or audio. */
export async function claimChatNotification(userId: string, eventId: string, factory: IDBFactory | undefined = globalThis.indexedDB): Promise<boolean> {
  if (!factory) return false;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => { if (!settled) { settled = true; resolve(value); } };
    const request = factory.open('till-chat-notifications', 1);
    request.onupgradeneeded = () => { request.result.createObjectStore('claims', { keyPath: 'key' }).createIndex('userId', 'userId'); };
    request.onerror = () => finish(false);
    request.onblocked = () => finish(false);
    request.onsuccess = () => {
      const db = request.result;
      if (settled) { db.close(); return; }
      const tx = db.transaction('claims', 'readwrite');
      tx.objectStore('claims').add({ key: `${userId}:${eventId}`, userId });
      tx.oncomplete = () => { db.close(); finish(true); };
      tx.onabort = tx.onerror = () => { db.close(); finish(false); };
    };
  });
}
export async function purgeChatNotificationClaims(userId: string): Promise<void> {
  if (!globalThis.indexedDB) return;
  await new Promise<void>((resolve) => {
    const request = indexedDB.open('till-chat-notifications', 1);
    request.onupgradeneeded = () => { request.result.createObjectStore('claims', { keyPath: 'key' }).createIndex('userId', 'userId'); };
    request.onerror = request.onblocked = () => resolve();
    request.onsuccess = () => {
      const db = request.result, tx = db.transaction('claims', 'readwrite');
      const cursor = tx.objectStore('claims').index('userId').openCursor(IDBKeyRange.only(userId));
      cursor.onsuccess = () => { const row = cursor.result; if (row) { row.delete(); row.continue(); } };
      tx.oncomplete = tx.onabort = tx.onerror = () => { db.close(); resolve(); };
    };
  });
}

/** Each connection starts silent; paginated catchup is silent through its FINAL cursor. */
export class ChatLiveBoundary {
  private ready = false;
  reset() { this.ready = false; }
  cursor(more: boolean) { if (!more) this.ready = true; }
  accepts(kind: string) { return this.ready && kind === 'posted'; }
  /** Catch-up after (re)connect finished (`cursor` with `more: false`). */
  get live() { return this.ready; }
}

export type ChatCoordination = { kind: 'state' | 'purge'; userId: string; channels?: string[] };
export function openChatCoordination(onMessage: (value: ChatCoordination) => void) {
  const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('till-chat-state');
  if (channel) channel.onmessage = (event: MessageEvent<ChatCoordination>) => {
    const value = event.data;
    if ((value?.kind === 'state' || value?.kind === 'purge') && typeof value.userId === 'string' && (value.channels === undefined || (Array.isArray(value.channels) && value.channels.every((id) => typeof id === 'string')))) onMessage(value);
  };
  return { post: (value: ChatCoordination) => channel?.postMessage(value), close: () => channel?.close() };
}

/** A short local WAV: no request, tracking, queued playback or OS permission changes. */
export function createChatAudio(): HTMLAudioElement {
  const samples = 4800, bytes = new Uint8Array(44 + samples * 2), view = new DataView(bytes.buffer);
  const word = (offset: number, value: string) => [...value].forEach((c, i) => bytes[offset + i] = c.charCodeAt(0));
  word(0, 'RIFF'); view.setUint32(4, bytes.length - 8, true); word(8, 'WAVE'); word(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true); view.setUint32(24, 24000, true); view.setUint32(28, 48000, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true); word(36, 'data'); view.setUint32(40, samples * 2, true);
  for (let i = 0; i < samples; i++) view.setInt16(44 + i * 2, Math.sin(i * 2 * Math.PI * 740 / 24000) * Math.sin(Math.PI * i / samples) * 5000, true);
  const audio = new Audio(`data:audio/wav;base64,${btoa(String.fromCharCode(...bytes))}`);
  audio.preload = 'auto'; return audio;
}
